import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogOverlay, Dropdown } from '@shopflo/ui'
import { ChevronDown, ChevronUp, X } from 'react-feather'
import { useSupportPageContext } from 'src/lib/contexts/SupportPageContext'
import dynamic from 'next/dynamic'
import { extractTextFromADF, getMediaLinks } from 'src/lib/utils/support'
import { useMemo } from 'react'
import RequestListBadge from './RequestListBadge'
import { CommentsType, JSONDocNode } from 'src/lib/types/support'
import { Accordion, AccordionContent, AccordionItem } from '@shopflo/ui'
import { useLocale } from 'src/lib/hooks/useLocal'
import FilePreviewGallery from 'src/components/common/form-elements/MultiFileUploadPreview'
import { ADFEditor, filterMediaNodes } from './ADFEditor'

// const ADFEditor = dynamic(() => import('src/components/support/sub-components/ADFEditor'), {
//   ssr: false,
//   loading: () => <ADFEditorSkeleton />
// })

const ADFEditorSkeleton = () => {
  return <div className='w-full bg-gray-200 h-20 animate-pulse rounded'></div>
}

const ViewFeatureDialog = () => {
  const {
    state: { featureSuggestionForm, isViewFeatureDialogOpen },
    actions: { handleSetViewFeatureDialogOpen }
  } = useSupportPageContext()

  const [open, setOpen] = useState(false)

  const mediaFiles = useMemo(() => {
    return getMediaLinks(featureSuggestionForm.attachment)
  }, [featureSuggestionForm.attachment])
  const { t } = useLocale()
  return (
    <Dialog open={isViewFeatureDialogOpen}>
      <DialogOverlay className='fixed inset-0 bg-black/50 z-40' />
      <DialogContent className='sm:max-w-[670px] bg-white text-black z-50 fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-2xl '>
        <DialogHeader>
          <DialogTitle>
            <div className='flex justify-between items-center'>
              <p className='text-coal-dark font-semibold flex gap-3'>
                <span>{t('view_feature_request')}</span>
              </p>
              <X onClick={() => handleSetViewFeatureDialogOpen(false)} className='cursor-pointer' />
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className='overflow-auto max-h-[500px] flex flex-col gap-3 p-2 overflow-x-hidden'>
          <div className='flex flex-col border-1 border-gray-light p-4 rounded-xl gap-2'>
            <div className='flex gap-3 items-center'>
              <h3 className='bg-gray-light border-[1.5px] border-gray-lighter px-4 py-2 w-fit rounded-xl'>
                #{featureSuggestionForm.issueId.substring(4)}
              </h3>
              <h3 className='text-coal-dark font-medium text-lg'>{featureSuggestionForm.summary}</h3>
            </div>

            <div className='flex  bg-gray-lightest px-3 py-2 rounded-lg gap-3 items-center font-normal text-sm'>
              <p className='text-gray-dark '>
                Created on{' '}
                <span className='text-sm text-coal-light font-medium ml-1'>{featureSuggestionForm.createdOn}</span>
              </p>

              {featureSuggestionForm.userDetails?.name && (
                <>
                  <span className='text-gray-dark'>by</span>
                  <p className='text-coal-light font-medium'>{featureSuggestionForm.userDetails?.name}</p>
                </>
              )}
            </div>
            <div className='w-fit'>
              <RequestListBadge type={featureSuggestionForm.status.name} count={0} isViewMode={true} />
            </div>

            <ADFEditor readOnly={true} value={featureSuggestionForm.description} />

            <FilePreviewGallery initialUrls={mediaFiles} readOnly={true} />

            <div className='mt-3'>
              {!open && (
                <button
                  className='text-sm font-medium text-coal-light border-1 rounded-md px-4 py-2 flex gap-2 items-center'
                  onClick={() => setOpen(true)}
                >
                  <span>{t('support.view_form')}</span>
                  <ChevronDown size={16} />
                </button>
              )}

              <Accordion
                type='single'
                collapsible
                value={open ? 'item-1' : undefined}
                onValueChange={val => setOpen(val === 'item-1')}
              >
                <AccordionItem value='item-1' className='border-none'>
                  <AccordionContent className='pt-4 space-y-4'>
                    <ReadOnlyEditorField label={t('support.impact_area')} value={featureSuggestionForm.impactArea} />
                    <ReadOnlyEditorField label={t('support.impact_area')} value={featureSuggestionForm.impactArea} />
                    <ReadOnlyEditorField
                      label={t('support.manaing_issue')}
                      value={featureSuggestionForm.currentResolution}
                    />
                    <ReadOnlyEditorField
                      label={t('support.proposed_solution')}
                      value={featureSuggestionForm.suggestedSolve}
                    />

                    <button
                      className='text-sm font-medium text-coal-light border-1 rounded-md px-4 py-2 flex gap-2 items-center  '
                      onClick={() => setOpen(false)}
                    >
                      <span>{t('support.hide_form')}</span>
                      <ChevronUp size={16} />
                    </button>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
          <Comments comments={featureSuggestionForm.comments} />
        </div>

        <DialogFooter>
          <div className='flex justify-end gap-3'>
            <button
              onClick={() => handleSetViewFeatureDialogOpen(false)}
              className='px-3 py-2 bg-white text-coal-dark rounded-md border-1 border-coal-lighter cursor-pointer text-sm font-semibold'
            >
              {t('close')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ViewFeatureDialog

interface CommentsProps {
  comments: CommentsType[]
}

const Comments: React.FC<CommentsProps> = ({ comments }) => {
  if (comments.length === 0) {
    return <></>
  }
  return (
    <div className='flex flex-col gap-3'>
      <h3>Comments</h3>
      <div className='flex flex-col gap-3'>
        {comments.map((comment, index) => (
          <div key={index} className='flex flex-col bg-gray-lightest border-gray-light border-1 p-3 rounded-xl gap-2'>
            <p className='text-sm text-gray-dark font-medium'>{comment.createdAt}</p>
            <div className={`p-2 font-normal w-full`} style={{ whiteSpace: 'pre-wrap' }}>
              {comment.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ReadOnlyEditorFieldProps {
  label: string
  value: JSONDocNode
}

const ReadOnlyEditorField: React.FC<ReadOnlyEditorFieldProps> = ({ label, value }) => {
  return (
    <div>
      <p className='text-sm font-medium'>{label}</p>
      <p className='text-sm bg-gray-lightest rounded-md mt-1 items-center flex'>
        <ADFEditor readOnly={true} value={value} />
      </p>
    </div>
  )
}
